import { useState, useEffect } from 'react';
import {
  WaterTower,
  Pump,
  LiftStationWetWell,
  RunTimeChart,
  TrendWidget,
  Controller,
  LiftStationWidget,
} from 'src/features/widgets/components';
import { WidgetType, WidgetTypeEnum, WidgetDataTypeEnum } from '../../types/types';
import { AssetType } from 'src/types/assets';
import { ControllersService } from 'src/services/controllers/controllers.service';
import { AssetService } from 'src/services/assets/asset.service';

import {
  useWaterLevelData,
  usePumpRuntimeData,
  useCycleCountData,
  useTrendWidgetData,
  useAssetStateData,
} from '../../hooks';
import { WIDGET_TYPES_CONNECTED_TO_ASSET } from '../../config';
import { useAuthentication } from 'src/features/authentication/context';
import { getTitleForTrendWidgets } from '../../utils';
import { CycleCountChart } from 'src/features/widgets/components/CycleCountChart';
import { Valve } from 'src/features/widgets/components/Valve/Valve';
import { useSite } from 'src/contexts/siteContext/sites.context';
import { LiveDataWidget } from 'src/features/widgets/components/LiveDataWidget';
import { useResponsive } from 'src/hooks/useResponsive';
import { Controller as ControllerType } from 'src/types/controllers/controller';
import { TagResponse } from 'src/types/tags/tags';
import { AssetController } from 'src/features/widgets/components/AssetController/AssetController';

type WidgetComponentType = {
  widget: WidgetType;
  isEditMode: boolean;
  toggleAddOrRemoveHandler: () => void;
  gatewayInputs: TagResponse[];
  isAddedController?: boolean;
  areGatewatInputsReady: boolean;
  isDesktop: boolean;
  position: {
    x: number;
    y: number;
  };
};

export const WidgetComponent = ({
  widget,
  isEditMode,
  toggleAddOrRemoveHandler,
  gatewayInputs,
  isAddedController,
  areGatewatInputsReady,
  isDesktop,
  position,
}: WidgetComponentType) => {
  const {
    customerId: { value: customerId },
    siteId: { value: siteId },
    user,
  } = useAuthentication();
  const [asset, setAsset] = useState<AssetType>();
  const [controller, setController] = useState<ControllerType>({
    id: '',
    controllerName: '',
    controllerType: '',
    gatewayId: '',
  });
  const { connectedTo } = widget;
  const isMobile = useResponsive('down', 'md');
  const { site } = useSite();
  const siteTimeZone = site?.location?.timeZone;

  const { dailyCycleCounts } = useCycleCountData({
    customerId: customerId,
    siteId: siteId,
    asset: asset!,
  });

  const { waterLevel, height, alarms, timestamp, unit } = useWaterLevelData({
    asset: asset!,
    customerId: customerId,
  });
  const { assetState, assetActiveAlarm } = useAssetStateData({ asset: asset! });
  const { dailyRuntimes } = usePumpRuntimeData({
    customerId: customerId,
    siteId: siteId,
    asset: asset!,
  });
  const gatewayInput = gatewayInputs?.find((input) => input.id === connectedTo);

  const { inputData } = useTrendWidgetData({
    gatewayInput: gatewayInput!,
    customerId: customerId,
    user: user,
  });

  useEffect(() => {
    const controllerWidgetType = widget.widgetType === WidgetTypeEnum.CONTROLLER_WIDGET;

    if (!customerId || !siteId || !isAddedController || !connectedTo || !controllerWidgetType)
      return;

    ControllersService.getById(customerId, siteId, connectedTo).then((response) => {
      setController(response);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedTo, customerId, siteId, isAddedController]);

  useEffect(() => {
    if (!customerId || !siteId) return;

    const assetId =
      typeof widget.connectedTo === 'string' ? widget.connectedTo : widget.connectedTo?.assetId;

    if (WIDGET_TYPES_CONNECTED_TO_ASSET.includes(widget.widgetType))
      AssetService.getAssetById({
        customerId: customerId,
        siteId: siteId,
        assetId: assetId,
      })
        .then((data) => setAsset(data))
        .catch((error) => console.error(error));
  }, [customerId, siteId, widget.connectedTo, widget.widgetType]);

  switch (widget.widgetType) {
    case WidgetTypeEnum.TOWER:
      return (
        <WaterTower
          widgetId={widget.id}
          title={asset ? asset.assetName : ''}
          height={height}
          waterLevel={waterLevel}
          unit={unit}
          alarms={alarms}
          isEditMode={isEditMode}
          testId={`towe-widget-${asset?.assetName}`}
          toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
          asset={asset}
          timestamp={timestamp}
        />
      );
    case WidgetTypeEnum.PUMP:
      return (
        <Pump
          widgetSubType={widget.widgetSubType}
          title={asset ? asset.assetName : ''}
          runMode={assetState}
          mode={''}
          isEditMode={isEditMode}
          widgetId={widget.id}
          testId="pump-widet"
          toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
          asset={asset}
          assetActiveAlarm={assetActiveAlarm}
        />
      );
    case WidgetTypeEnum.LIFT_STATION_WET_WELL:
      return (
        <LiftStationWetWell
          height={height}
          waterLevel={waterLevel}
          title={asset ? asset.assetName : ''}
          unit={unit}
          alarms={alarms!}
          isEditMode={isEditMode}
          widgetId={widget.id}
          testId="wet-well-widget"
          toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
          asset={asset}
          timestamp={timestamp}
        />
      );
    case WidgetTypeEnum.BARS_GRAPH:
      if (widget.widgetDataType === WidgetDataTypeEnum.RUNTIME) {
        return (
          <RunTimeChart
            title={asset ? `${asset.assetName} -` : ''}
            values={
              dailyRuntimes !== null
                ? dailyRuntimes!.map((x) => ({ label: x.day, value: x.value }))
                : null
            }
            isEditMode={isEditMode}
            widgetId={widget.id}
            testId="bars-graph-widget"
            toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
            asset={asset}
            siteTimeZone={siteTimeZone}
          />
        );
      } else if (widget.widgetDataType === WidgetDataTypeEnum.DAILY_CYCLE_COUNT) {
        return (
          <CycleCountChart
            title={asset ? `${asset.assetName} -` : ''}
            values={
              dailyCycleCounts !== null
                ? dailyCycleCounts!.map((x) => ({ label: x.day, value: x.value }))
                : null
            }
            isEditMode={isEditMode}
            widgetId={widget.id}
            testId="bars-graph-widget"
            toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
            asset={asset}
            siteTimeZone={siteTimeZone}
          />
        );
      } else {
        return (
          <RunTimeChart
            title={asset ? `${asset.assetName} -` : ''}
            values={
              dailyRuntimes !== null
                ? dailyRuntimes!.map((x) => ({ label: x.day, value: x.value }))
                : null
            }
            isEditMode={isEditMode}
            widgetId={widget.id}
            testId="bars-graph-widget"
            toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
            asset={asset}
            siteTimeZone={siteTimeZone}
          />
        );
      }

    case WidgetTypeEnum.SPIRAL_DOTS_GRAPH:
      return (
        <TrendWidget
          type="continuous"
          title={getTitleForTrendWidgets(gatewayInput)}
          unit={gatewayInput?.rawUnit ?? ''}
          isEditMode={isEditMode}
          widgetId={widget.id}
          testId="spiral-dots-graph-widget"
          toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
          values={inputData}
          gatewayInput={gatewayInput}
        />
      );

    case WidgetTypeEnum.LIVE_DATA:
      return (
        <LiveDataWidget
          title={widget.title ?? 'Live Data'}
          isDataReady={areGatewatInputsReady}
          selectedTagIds={widget.tags}
          widgetId={widget.id}
          toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
          testId="live-data-widget"
          isEditMode={isEditMode}
          isMobile={isMobile}
          pos={widget.positioning}
          gatewayInputs={gatewayInputs}
        />
      );
    case WidgetTypeEnum.LINEAR_GRAPH:
      return (
        <TrendWidget
          type="discrete"
          title={getTitleForTrendWidgets(gatewayInput)}
          unit={gatewayInput?.rawUnit ?? ''}
          isEditMode={isEditMode}
          widgetId={widget.id}
          testId="linear-graph-widget"
          toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
          values={inputData}
          gatewayInput={gatewayInput}
        />
      );
    case WidgetTypeEnum.CONTROLLER_WIDGET:
      return (
        <Controller
          title={controller?.controllerName}
          controllerId={controller?.id}
          gatewayId={controller?.gatewayId}
          testId="controller-widget"
          isEditMode={isEditMode}
          widgetId={widget.id}
          toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
        />
      );
    case WidgetTypeEnum.VALVE:
      return (
        <Valve
          widgetId={widget.id}
          isEditMode={isEditMode}
          toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
          title={asset ? asset.assetName : ''}
          asset={asset}
          testId="valve-widget"
        />
      );

    case WidgetTypeEnum.LIFT_STATION_WIDGET:
      return (
        <LiftStationWidget
          toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
          widgetId={widget.id}
          title={widget.title}
          isDesktop={isDesktop}
          gatewayInputs={gatewayInputs}
          connectedTo={connectedTo}
          position={position}
          widget={widget}
          isMobile={isMobile}
        />
      );

    case WidgetTypeEnum.ASSET_CONTROLLER:
      return (
        <AssetController
          widget={widget}
          toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
          gatewayInputs={gatewayInputs}
          asset={asset}
          runMode={assetState}
          assetActiveAlarm={assetActiveAlarm}
          isEditMode={isEditMode}
          waterLevel={waterLevel}
          height={height}
          unit={unit}
        />
      );

    default:
      return null;
  }
};
